import './Sidebar.css';
import { signOut } from 'firebase/auth';
import { auth } from './firebase';
import { obtenerMayoresDiferencias } from './api/catalogo';
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  Car, FileText, Heart, LogOut, User, BarChart3, Bot,
  Wrench, Users, TrendingUp, LayoutGrid, LayoutDashboard, Sliders, Brain,
} from 'lucide-react';
import { useUserRole } from './useUserRole';
import { ROLE_META, routesForRole } from './roles';

const ICON_MAP = {
  LayoutGrid: <LayoutGrid size={16} />,
  LayoutDashboard: <LayoutDashboard size={16} />,
  Sliders: <Sliders size={16} />,
  Brain: <Brain size={16} />,
  TrendingUp: <TrendingUp size={16} />,
  Car: <Car size={16} />,
  BarChart3: <BarChart3 size={16} />,
  Bot: <Bot size={16} />,
  Heart: <Heart size={16} />,
  User: <User size={16} />,
  Users: <Users size={16} />,
  Wrench: <Wrench size={16} />,
  FileText: <FileText size={16} />,
};

function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, rol } = useUserRole();
  const [hasNuevas, setHasNuevas] = useState(false);

  useEffect(() => {
    let activo = true;
    obtenerMayoresDiferencias().then((data) => {
      if (!activo) return;
      if (!data?.generadoEn) { setHasNuevas(false); return; }
      const lastMs = Number(localStorage.getItem('lastSeenDiferenciasTs') || 0);
      setHasNuevas(!lastMs || data.generadoEn > lastMs);
    }).catch((e) => console.error(e));
    return () => { activo = false; };
  }, []);

  const handleLogout = async () => {
    try { await signOut(auth); navigate('/login'); }
    catch (e) { console.error(e); }
  };

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  // Agrupar rutas por sección según el rol actual
  const routes = rol ? routesForRole(rol) : [];
  const sections = routes.reduce((acc, r) => {
    const sec = r.section || 'General';
    if (!acc[sec]) acc[sec] = [];
    acc[sec].push(r);
    return acc;
  }, {});

  const meta = rol ? ROLE_META[rol] : null;

  return (
    <aside className="sidebar">

      {/* Logo */}
      <div className="sidebar-brand">
        <div className="sidebar-brand-icon"><Car size={18} /></div>
        <span className="sidebar-brand-name">DoctorCar</span>
      </div>

      {/* Badge de perfil */}
      {meta && (
        <div className="sidebar-role-badge">
          <span className="sidebar-role-dot" style={{ background: meta.color }} />
          {meta.label}
        </div>
      )}

      {/* Nav dinámica */}
      <nav className="sidebar-nav">
        {Object.entries(sections).map(([secName, secRoutes]) => (
          <div className="sidebar-section" key={secName}>
            <span className="sidebar-section-label">{secName}</span>
            {secRoutes.map((route) => (
              <React.Fragment key={route.key}>
                <Link
                  to={route.key === 'inteligencia' ? '/inteligencia/dashboard' : route.path}
                  className={`sidebar-link ${isActive(route.path) ? 'active' : ''}`}
                >
                  <span className="sidebar-link-icon">
                    {ICON_MAP[route.icon] || <LayoutGrid size={16} />}
                  </span>
                  <span>{route.label}</span>
                  {route.key === 'novedades' && hasNuevas && (
                    <span className="sidebar-badge">Nuevo</span>
                  )}
                  {route.badge && (
                    <span className="sidebar-badge sidebar-badge-beta">{route.badge}</span>
                  )}
                </Link>

                {/* Sub-tabs de Inteligencia PM */}
                {route.key === 'inteligencia' && isActive('/inteligencia') && (
                  <div className="sidebar-subtabs">
                    {[
                      { tab: 'dashboard', label: 'Dashboard', roles: ['completo', 'superadmin'] },
                      { tab: 'comparador', label: 'Comparador', roles: ['completo', 'superadmin'] },
                      { tab: 'simulador', label: 'Simulador', roles: ['completo', 'superadmin'] },
                      { tab: 'leads', label: 'Leads', roles: ['superadmin'] },
                    ].filter(t => t.roles.includes(rol))
                      .map(({ tab, label }) => (
                        <Link
                          key={tab}
                          to={`/inteligencia/${tab}`}
                          className={`sidebar-subtab ${location.pathname === `/inteligencia/${tab}` ? 'active' : ''}`}
                        >
                          {label}
                        </Link>
                      ))}
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="sidebar-footer">
        {user?.email && (
          <div className="sidebar-user-email" title={user.email}>
            {user.email}
          </div>
        )}
        <button className="sidebar-logout" onClick={handleLogout}>
          <LogOut size={15} /><span>Cerrar sesión</span>
        </button>
      </div>

    </aside>
  );
}

export default Sidebar;